import { useMemo } from 'react';

import { centsToDollar } from '@givebacks/utils/currency';
import { Card, Group, Skeleton, Stack, SwitchCard, Text, ThemeIcon, Tooltip } from 'ui';
import { IconInfo, IconRewardsLeft } from 'ui/Icons';

import classes from './ApplyRewardsCard.module.css';

type ApplyRewardsCardProps = {
  availableRewards: number;
  selectedRewards: number;
  onChange: (selected: boolean) => void;
  /** When present, this string will be displayed in lieu of the selected rewards */
  unavailableToApply?: string;
  /** When true, the card will be displayed in a loading state */
  isInitialLoading?: boolean;
  /** When true, the card will be displayed in a pending state (removing or applying rewards) */
  isPending?: 'removing' | 'applying';
};

interface ApplyRewardsContentProps
  extends Pick<
    ApplyRewardsCardProps,
    'availableRewards' | 'selectedRewards' | 'unavailableToApply' | 'isPending'
  > {
  withTooltipIcon?: boolean;
}

const BalanceText = ({
  availableRewards,
  selectedRewards,
  unavailableToApply,
  isPending,
}: ApplyRewardsContentProps) => {
  const pendingText = useMemo(() => {
    if (isPending === 'removing') {
      return 'Removing rewards...';
    } else if (isPending === 'applying') {
      return 'Applying rewards...';
    }

    return undefined;
  }, [isPending]);

  const rewardsText = useMemo(() => {
    if (unavailableToApply) {
      return unavailableToApply;
    }
    if (selectedRewards > 0) {
      return `${centsToDollar(selectedRewards)} applied`;
    }
    return `${centsToDollar(availableRewards)} available`;
  }, [unavailableToApply, selectedRewards, availableRewards]);

  return (
    <Text fz="14px" fw={500} ta="left" c="gray.8">
      {pendingText ? pendingText : rewardsText}
    </Text>
  );
};

const ApplyRewardsContent = ({
  availableRewards,
  selectedRewards,
  unavailableToApply,
  withTooltipIcon = false,
  isPending = undefined,
}: ApplyRewardsContentProps) => {
  return (
    <Group justify="space-between">
      <Group gap="sm">
        <ThemeIcon size="xl" radius="sm" variant="transparent">
          <IconRewardsLeft className={classes.ApplyRewardsCard__icon} />
        </ThemeIcon>
        <Stack gap="4px">
          <Text fz="11" fw={400} ta="left" c="gray.6">
            Apply Rewards
          </Text>

          <BalanceText
            availableRewards={availableRewards}
            selectedRewards={selectedRewards}
            unavailableToApply={unavailableToApply}
            isPending={isPending}
          />
        </Stack>
      </Group>
      {withTooltipIcon && (
        <ThemeIcon
          size="md"
          c="gray.6"
          radius="sm"
          variant="transparent"
          style={{ alignSelf: 'flex-start', justifySelf: 'flex-end' }}
        >
          <IconInfo size={16} />
        </ThemeIcon>
      )}
    </Group>
  );
};

const ApplyRewardsCardLoading = () => {
  return (
    <Card className={classes.ApplyRewardsCard}>
      <Group wrap="nowrap">
        <ThemeIcon size="xl" radius="sm" variant="transparent">
          <IconRewardsLeft className={classes.ApplyRewardsCard__icon} />
        </ThemeIcon>
        <Stack gap="xs">
          <Skeleton height={12} width="50px" radius="sm" />
          <Skeleton height={12} width="140px" radius="sm" />
        </Stack>
        <Skeleton height={25} width="50px" radius="sm" />
      </Group>
    </Card>
  );
};

const ApplyRewardsCard = ({
  availableRewards,
  selectedRewards,
  unavailableToApply,
  onChange,
  isInitialLoading = false,
  isPending = undefined,
}: ApplyRewardsCardProps) => {
  const isChecked = useMemo(() => {
    return selectedRewards > 0;
  }, [selectedRewards]);

  if (isInitialLoading) {
    return <ApplyRewardsCardLoading />;
  }
  if (unavailableToApply || availableRewards <= 0) {
    return (
      <Tooltip
        label={
          <Text fz="xs">
            Every time you shop or dine at participating merchants, you earn Rewards! Enroll in
            Rewards to help cover costs like school fees, event tickets, and more{' '}
            {unavailableToApply && (
              <Text fz="xs" fw={600} span>
                (recurring and pledge donations excluded)
              </Text>
            )}
            .
          </Text>
        }
        maw={400}
      >
        <Card className={classes.ApplyRewardsCard}>
          <ApplyRewardsContent
            availableRewards={availableRewards}
            selectedRewards={selectedRewards}
            unavailableToApply={unavailableToApply}
            withTooltipIcon={true}
          />
        </Card>
      </Tooltip>
    );
  }
  return (
    <SwitchCard
      className={classes.ApplyRewardsCard}
      subtitle={
        <ApplyRewardsContent
          availableRewards={availableRewards}
          selectedRewards={selectedRewards}
          isPending={isPending}
        />
      }
      checked={isChecked}
      onChange={(checked) => (isPending ? undefined : onChange(checked))}
      disabled={Boolean(isPending)}
    />
  );
};

export { ApplyRewardsCard };
